import { useQuery } from "@tanstack/react-query";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { StatCard } from "@/components/stat-card";
import { apiGet } from "@/lib/api-client";
import { asCurrency, asPercent } from "@/lib/format";
import { asRecord, asArray } from "@/lib/type-utils";

/**
 * Honest numeric formatter: an absent/unparseable field renders "n/a",
 * never a fabricated 0.00. A real zero from a successful read still
 * renders as 0.
 */
function asMetric(value: unknown, digits: number, suffix = ""): string {
  if (value === undefined || value === null) {
    return "n/a";
  }
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(digits)}${suffix}` : "n/a";
}

export function AnalyticsPage() {
  const calibrationQuery = useQuery({
    queryKey: ["analytics", "calibration"],
    queryFn: () => apiGet<unknown>("/api/calibration/metrics"),
    refetchInterval: 60_000,
  });

  const backtestQuery = useQuery({
    queryKey: ["analytics", "backtests"],
    queryFn: () => apiGet<unknown>("/api/backtest/history"),
    refetchInterval: 120_000,
  });

  const performanceQuery = useQuery({
    queryKey: ["analytics", "performance"],
    queryFn: () => apiGet<unknown>("/api/performance/summary"),
    refetchInterval: 60_000,
  });

  const calibration = asRecord(calibrationQuery.data);
  // /api/backtest/history wraps results in a {runs, count} envelope.
  const backtests = asArray(asRecord(backtestQuery.data).runs);
  const performance = asRecord(performanceQuery.data);

  const brierScore = Number(calibration.brier_score ?? 0);
  const totalPredictions = Number(calibration.total_predictions ?? calibration.count ?? 0);
  const calibrationCurve = asArray(calibration.curve);

  const winRate = Number(performance.win_rate ?? performance.accuracy ?? 0);
  const profitFactor = Number(performance.profit_factor ?? 0);
  const sharpeRatio = Number(performance.sharpe_ratio ?? performance.risk_adjusted_return ?? 0);

  const sortedBacktests = [...backtests].sort(
    (a, b) =>
      new Date(String(b.created_at ?? b.date ?? "")).getTime() -
      new Date(String(a.created_at ?? a.date ?? "")).getTime(),
  );

  return (
    <div className="space-y-4 md:space-y-6">
      <Panel title="Advanced Analytics" description="Calibration metrics, backtesting results, and trading performance.">
        {calibrationQuery.isError || performanceQuery.isError ? (
          <QueryErrorState
            what="performance metrics"
            onRetry={() => {
              void calibrationQuery.refetch();
              void performanceQuery.refetch();
            }}
          />
        ) : (
          <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
            <StatCard
              label="Brier Score"
              value={brierScore.toFixed(3)}
              tone={brierScore < 0.2 ? "success" : brierScore < 0.3 ? "warning" : "default"}
              hint="Lower is better (0 = perfect)"
            />
            <StatCard
              label="Total Predictions"
              value={String(totalPredictions)}
              hint="Predictions with outcomes"
            />
            <StatCard
              label="Win Rate"
              value={asPercent(winRate)}
              tone={winRate > 0.55 ? "success" : winRate > 0.45 ? "warning" : "default"}
              hint="Trade success rate"
            />
            <StatCard
              label="Profit Factor"
              value={asMetric(performance.profit_factor, 2)}
              tone={profitFactor > 1.5 ? "success" : profitFactor > 1 ? "warning" : "default"}
              hint="Gross profit / gross loss"
            />
            <StatCard
              label="Sharpe Ratio"
              value={sharpeRatio.toFixed(2)}
              tone={sharpeRatio > 1 ? "success" : sharpeRatio > 0.5 ? "warning" : "default"}
              hint="Risk-adjusted return"
            />
          </div>
        )}
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Calibration Curve" description="Predicted vs Actual outcomes">
          <div className="rounded-lg bg-gunmetal/50 p-3 md:p-4">
            {calibrationCurve.length === 0 ? (
              <div className="flex h-48 md:h-64 items-center justify-center text-sm text-slate-400">
                No calibration data available
              </div>
            ) : (
              <div className="space-y-3 md:space-y-4 overflow-x-auto">
                <div className="grid grid-cols-5 gap-2 text-center text-xs text-slate-400">
                  <div>Predicted</div>
                  <div>Actual</div>
                  <div>Count</div>
                  <div>Error</div>
                  <div>Bucket</div>
                </div>
                {calibrationCurve.slice(0, 10).map((point, index) => {
                  const predicted = Number(point.predicted ?? point.prediction ?? 0);
                  const actual = Number(point.actual ?? point.outcome ?? 0);
                  const count = Number(point.count ?? point.n ?? 0);
                  const error = Math.abs(predicted - actual);
                  const bucket = index * 10;

                  return (
                    <div key={index} className="grid grid-cols-5 gap-2 text-center text-sm">
                      <div className="font-medium">{asPercent(predicted)}</div>
                      <div className={Math.abs(predicted - actual) < 0.1 ? "text-green-400" : "text-yellow-400"}>
                        {asPercent(actual)}
                      </div>
                      <div className="text-slate-400">{count}</div>
                      <div className={error < 0.1 ? "text-green-400" : "text-red-400"}>{error.toFixed(2)}</div>
                      <div className="text-slate-400">{bucket}-{bucket + 10}%</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Panel>

        <Panel title="Recent Backtests" description="Strategy backtesting results">
          <div className="max-h-[400px] space-y-2 overflow-y-auto">
            {backtestQuery.isError ? (
              <QueryErrorState what="backtest history" onRetry={() => void backtestQuery.refetch()} />
            ) : sortedBacktests.length === 0 ? (
              <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
                No backtest results available
              </div>
            ) : (
              sortedBacktests.slice(0, 10).map((backtest, index) => {
                // backtest_runs columns: created_at, total_return_pct,
                // sharpe_ratio, total_trades, win_rate (0-1), strategy.
                const pnl = Number(backtest.total_return_pct ?? backtest.pnl ?? backtest.profit ?? 0);
                const returnPct = Number(backtest.total_return_pct ?? backtest.return_pct ?? backtest.return ?? 0);

                return (
                  <div key={index} className="rounded-lg bg-gunmetal/50 p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm font-medium">{String(backtest.strategy || backtest.name || "Unknown Strategy")}</div>
                        <div className="mono mt-1 text-xs text-slate-400">
                          {String(backtest.created_at || backtest.date || backtest.run_date || "Unknown")}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-semibold ${pnl >= 0 ? "text-green-400" : "text-red-400"}`}>
                          {pnl >= 0 ? "+" : ""}
                          {pnl.toFixed(2)}%
                        </div>
                        <div className="text-xs text-slate-400">{asPercent(returnPct / 100)} return</div>
                      </div>
                    </div>
                    <div className="mt-2 flex gap-4 text-xs text-slate-400">
                      <span>Trades: {String(backtest.trades ?? backtest.total_trades ?? "—")}</span>
                      <span>Win Rate: {asPercent(Number(backtest.win_rate ?? 0))}</span>
                      <span>Sharpe: {Number(backtest.sharpe ?? backtest.sharpe_ratio ?? 0).toFixed(2)}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Panel>
      </div>

      <Panel title="📊 Performance Metrics" description="Key trading performance indicators">
        <div className="grid grid-cols-2 gap-2 md:gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {[
            // Absent fields render "n/a"/"—", never a fabricated 0.00 — the
            // backend omits trade-performance keys when the tracker is down.
            { label: "Total Trades", value: performance.total_trades == null ? "—" : asMetric(performance.total_trades, 0) },
            { label: "Winning Trades", value: String(performance.winning_trades ?? "—") },
            { label: "Losing Trades", value: String(performance.losing_trades ?? "—") },
            { label: "Avg Winner", value: asMetric(performance.avg_winner, 2, "%") },
            { label: "Avg Loser", value: asMetric(performance.avg_loser, 2, "%") },
            {
              label: "Max Drawdown",
              // The tracker reports max_drawdown as a dollar amount
              // (peak-to-trough cumulative P&L), not a percent.
              value: performance.max_drawdown == null ? "n/a" : asCurrency(Number(performance.max_drawdown)),
            },
            { label: "Expectancy", value: asMetric(performance.expectancy, 2) },
            {
              label: "Avg Hold Time",
              value: asMetric(performance.avg_hold_time ?? performance.avg_hold_hours, 1, "h"),
            },
            { label: "Profit/Month", value: asMetric(performance.profit_per_month, 2, "%") },
          ].map((metric, index) => (
            <div key={index} className="rounded-lg bg-gunmetal/50 p-2 md:p-3 text-center">
              <div className="text-sm md:text-lg font-bold text-gold-400">{metric.value}</div>
              <div className="text-[0.6rem] md:text-xs text-slate-400">{metric.label}</div>
            </div>
          ))}
        </div>
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Calibration API" description="Raw calibration metrics">
          <JsonView value={calibrationQuery.data} />
        </Panel>
        <Panel title="Performance API" description="Raw performance data">
          <JsonView value={performanceQuery.data} />
        </Panel>
      </div>
    </div>
  );
}
